<template>
  <div>
    <v-card-text class="mt-4 pa-0">
      <v-alert
        :value="showInlogError"
        transition="scale-transition"
        dismissible
        class="message-wrapper error-message my-3"
        icon="$alert"
      >
        <p class="my-0">
          {{ $t("loginErrorMessage") }}
        </p>
      </v-alert>
      <v-form ref="loginForm" @submit.prevent>
        <v-list
          v-if="selectedUser"
          id="selected-username"
          class="usernames-list mb-3"
        >
          <v-list-item>
            <v-list-item-avatar class="gradient" size="34">
              <span class="white--text headline text-capitalize">{{
                selectedUser[0]
              }}</span>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="font-weight-bold">{{
                selectedUser
              }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider></v-divider>
        </v-list>
        <v-text-field
          v-else
          id="login-username-input"
          ref="usernameInput"
          v-model.trim="username"
          class="login-username-input"
          :rules="validationRules('userName')"
          inputmode="email"
          prepend-icon="$username"
          :label="$t('userName')"
          outlined
          autocomplete="off"
          validate-on-blur
          @keydown.space.prevent
          @keyup.enter="submitLoginForm"
        >
          <template #message="{ message }">
            {{ $t(message) }}
          </template>
        </v-text-field>
        <v-text-field
          id="login-password-input"
          ref="passwordInput"
          v-model.trim="password"
          :rules="validationRules('password')"
          prepend-icon="$password"
          outlined
          :type="showpw ? 'text' : 'password'"
          :label="$t('password')"
          validate-on-blur
          @keydown.space.prevent
          @keyup.enter="submitLoginForm"
        >
          <template #append-outer>
            <v-btn
              id="password-visibility-toggle"
              class="password-visibility-toggle icon-button"
              icon
              small
              @click="showpw = !showpw"
            >
              <v-icon
                v-text="showpw ? '$show_password' : '$hide_password'"
              ></v-icon>
            </v-btn>
          </template>
          <template #message="{ message }">
            {{ $t(message) }}
          </template>
        </v-text-field>
      </v-form>
    </v-card-text>
    <v-card-actions class="pa-0">
      <v-checkbox
        id="stay-signed-in-checkbox"
        v-model="staySignedIn"
        class="ma-0 pa-0"
        color="primary"
        hide-details
        :label="$t('stayLoggedInCheckboxLabel')"
      ></v-checkbox>
      <v-spacer></v-spacer>
      <v-btn
        id="login-button"
        class="button"
        color="primary"
        :disabled="disableLoginButton"
        @click="submitLoginForm"
        >{{ $t("login") }}
      </v-btn>
    </v-card-actions>
    <v-card-actions class="pa-0 my-3">
      <a
        v-if="loggedInUsernamesExist"
        id="to-user-selection"
        href="/"
        class="login-link"
        @click.prevent="showUserSelection"
      >
        <v-icon left small color="primary" class="ma-0"
          >$link_arrow_left</v-icon
        >
        {{ $t("loginUserSelection") }}</a
      >
      <v-spacer></v-spacer>
    </v-card-actions>
  </div>
</template>

<script>
import { axiosGet } from "@/api/requests";
import snackBarMessages from "@/mixins/snackBarMessages";

export default {
  name: "LoginForm",
  mixins: [snackBarMessages],
  props: {
    selectedUser: {
      type: String,
      required: false,
      default: null,
    },
    loggedInUsernamesExist: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      showpw: false,
      username: "",
      password: "",
      staySignedIn: false,
      showInlogError: false,
    };
  },
  computed: {
    disableLoginButton() {
      return this.username === "" || this.password === "";
    },
  },
  watch: {
    selectedUser: {
      immediate: true,
      handler(val) {
        this.username = val ? val : "";
      },
    },
  },
  methods: {
    submitLoginForm() {
      this.showInlogError = false;
      if (this.$refs.usernameInput) {
        this.$refs.usernameInput.blur();
      }
      this.$refs.passwordInput.blur();
      if (this.$refs.loginForm.validate()) {
        const isprod = process.env.VUE_APP_MODE === "PROD";
        this.$root.$loader.open(this.$t("login"));
        const url = process.env.VUE_APP_IDENTITY_URL;
        axiosGet(url, null, {
          username: this.username,
          password: this.password,
          isprod: isprod,
        })
          .then((response) => {
            this.handleLoginResponse(response);
          })
          .catch((error) => {
            this.showInlogError = true;
          })
          .finally(() => {
            this.$root.$loader.close();
          });
      }
    },
    handleLoginResponse(response) {
      if (response.isAuthenticated) {
        if (
          [0, 3].includes(response.status) ||
          [0, 13, 14].includes(response.accessType) ||
          response.cloudData.length === 0
        ) {
          this.snackbarError(this.$t("noAccessErrorMessage"));
        } else {
          response.staySignedIn = this.staySignedIn;
          this.$emit("show-2fa", {
            user: response,
            username: this.username,
          });
        }
      } else {
        this.showInlogError = true;
      }
    },
    validationRules(fieldname) {
      const emailRegex =
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      switch (fieldname) {
        case "password":
          return [(v) => !!v || "requiredFieldError"];
        case "userName":
          return [
            (v) => !!v || "requiredFieldError",
            (v) => emailRegex.test(v) || "invalidUserNameError",
          ];
      }
    },
    showUserSelection() {
      this.showInlogError = false;
      this.$refs.loginForm.reset();
      this.$emit("select-usernames");
    },
  },
};
</script>
